import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';

// Safe navigation component that can be rendered both inside and outside Router context
const SafeNavigation = ({ returnPath, children }) => {
  try {
    // This will throw if not in Router context
    const navigate = useNavigate();
    return children(path => navigate(path));
  } catch (error) {
    // Fallback for when outside Router context
    return children(path => window.location.href = path);
  }
};

const ErrorPage = ({ 
  title = 'Document Error',
  message = 'An error occurred while loading this content.',
  showReload = true,
  showReturn = true,
  returnPath = '/workspaces',
  returnText = 'Return to Workspace',
  error = null
}) => {
  // If the message includes the specific error about null/undefined to object,
  // provide a more helpful message
  let displayMessage = message;
  if (message && message.includes('Cannot convert undefined or null to object')) {
    displayMessage = "Cannot convert undefined or null to object. This usually happens when the application tries to process data that's not in the expected format. Please try reloading the page or returning to the workspace.";
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#121218] text-gray-200">
      <div className="w-full max-w-2xl bg-[#1a1a1a] border border-[#2a2a36] rounded-lg shadow-lg p-8 space-y-6">
        <div className="flex items-center mb-2 text-red-400">
          <ExclamationTriangleIcon className="h-10 w-10 mr-4 flex-shrink-0" />
          <h1 className="text-2xl font-bold">{title}</h1>
        </div>
        
        <p className="text-gray-300 py-2 px-4 bg-[#24242c] border-l-4 border-red-500 rounded">
          {displayMessage}
        </p>
        
        {error && typeof error === 'object' && (
          <div className="mt-4 p-4 bg-[#24242c] border border-[#2a2a36] rounded-md text-left overflow-auto text-sm">
            <p className="font-medium text-red-400 mb-2">Error Details:</p>
            <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-x-auto">
              {error.message || JSON.stringify(error, null, 2)}
            </pre>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 mt-4">
          {showReload && (
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reload Page
            </button>
          )}
          
          {showReturn && (
            <SafeNavigation returnPath={returnPath}>
              {(navigateTo) => (
                <button 
                  onClick={() => navigateTo(returnPath)}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-[#24242c] hover:bg-[#2a2a36] border border-[#2a2a36] text-white rounded-md text-sm font-medium"
                >
                  <HomeIcon className="h-4 w-4" />
                  {returnText}
                </button>
              )}
            </SafeNavigation>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage; 